import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { TiltCard, GlowOrb, ParticleField, GlowingCard, FloatingElement } from '../components/3DEffects';
import { MOCK_PACKAGES } from '../data/mockData';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pricing() {
  const { user, login } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    if (user && !loading) {
      const selectedPkg = localStorage.getItem('selected_package');
      if (selectedPkg) {
        localStorage.removeItem('selected_package');
        handleSubscribe(selectedPkg);
      }
    }
  }, [user, loading]);

  const fetchPackages = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      const res = await fetch(`${API}/packages`, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setPackages(data);
            return;
          }
        }
      }
      console.warn('API returned invalid response for packages. Using fallback data.');
      setPackages(MOCK_PACKAGES);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Failed to fetch packages, using fallback:', error);
      setPackages(MOCK_PACKAGES);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const res = await fetch(`${API}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });

      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getIconForCategory = (category) => {
    const icons = {
      'Silver': '🛡️',
      'Gold': '👑',
      'Diamond': '💎'
    };
    return icons[category] || '⚡';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080B14' }}>
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 relative overflow-hidden" style={{ background: '#080B14' }}>
      {/* Background Effects */}
      <div className="absolute inset-0 z-0">
        <ParticleField count={25} />
        <GlowOrb color="blue" size={500} className="top-0 right-0 translate-x-1/3 -translate-y-1/3" />
        <GlowOrb color="midnight" size={400} className="bottom-0 left-0 -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <FloatingElement duration={8} yOffset={10}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium mb-6 shadow-[0_0_15px_rgba(59,130,246,0.1)]">
              <Sparkles className="w-4 h-4 text-blue-400 fill-blue-400/15" />
              Flexible Plans
            </span>
          </FloatingElement>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="pricing-title">
            Simple, Transparent Pricing
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Choose the perfect AMC plan for your business. All plans include 24/7 support 
            and dedicated account management.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center max-w-6xl mx-auto">
          {packages.map((pkg, idx) => {
            const isPriceVisible = user;

            const handleButtonClick = () => {
              if (user) {
                handleSubscribe(pkg.package_id);
              } else {
                localStorage.setItem('selected_package', pkg.package_id);
                toast.info('Please sign in to view pricing and subscribe.');
                login();
              }
            };

            return (
              <motion.div
                key={pkg.package_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <TiltCard intensity={8} className="h-full">
                  <GlowingCard
                    className={`relative rounded-2xl overflow-hidden h-full border ${
                      pkg.is_popular ? 'ring-1 ring-blue-500/30 border-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.15)]' : 'border-blue-500/10'
                    }`}
                    data-testid={`pricing-card-${pkg.package_id}`}
                  >
                    {pkg.is_popular && (
                      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs font-semibold text-center py-2 shadow-md">
                        Most Popular
                      </div>
                    )}

                    <div className={`p-6 ${pkg.is_popular ? 'pt-12' : ''}`}>
                      {/* Icon */}
                      <FloatingElement duration={6} yOffset={8} delay={idx * 0.2}>
                        <div className="text-4xl mb-4">{getIconForCategory(pkg.category)}</div>
                      </FloatingElement>

                      {/* Name & Description */}
                      <h3 className="text-xl font-semibold text-white mb-2">{pkg.name}</h3>
                      <p className="text-gray-400 text-sm mb-6 line-clamp-2">{pkg.description}</p>

                      {/* Price */}
                      <div className="mb-6 h-10 flex items-baseline">
                        {isPriceVisible ? (
                          <>
                            <span className="text-4xl font-bold text-white">₹{pkg.price}</span>
                            <span className="text-gray-500">/{pkg.billing_cycle}</span>
                          </>
                        ) : (
                          <span className="text-sm font-semibold text-blue-400 bg-blue-500/10 px-3 py-1.5 rounded-full border border-blue-500/15">
                            🔒 Sign in to view pricing
                          </span>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="space-y-3 mb-8">
                        {pkg.features?.slice(0, 5).map((feature, fidx) => (
                          <li key={fidx} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-300 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <Button
                        onClick={handleButtonClick}
                        disabled={checkoutLoading === pkg.package_id}
                        className={`w-full py-6 rounded-xl font-semibold transition-all duration-300 border-none ${
                          pkg.is_popular
                            ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white hover:shadow-[0_0_40px_rgba(59,130,246,0.4)] hover:opacity-95 shadow-[0_4px_12px_rgba(59,130,246,0.2)]'
                            : 'bg-white/5 text-white hover:bg-blue-500/10 border border-blue-500/15 hover:border-blue-500/40 shadow-sm'
                        }`}
                        data-testid={`subscribe-${pkg.package_id}`}
                      >
                        {checkoutLoading === pkg.package_id ? (
                          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            Get Started
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </div>
                  </GlowingCard>
                </TiltCard>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <h2 className="text-2xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: 'What is included in an AMC plan?',
                a: 'All plans include regular maintenance visits, 24/7 remote support, hardware diagnostics, and priority response times.'
              },
              {
                q: 'Can I upgrade my plan later?',
                a: 'Yes! You can upgrade anytime. We\'ll prorate the difference for the remaining billing period.'
              },
              {
                q: 'What is the minimum contract period?',
                a: 'Monthly plans have no lock-in. Annual plans offer significant discounts with a 12-month commitment.'
              },
              {
                q: 'Do you offer custom enterprise plans?',
                a: 'Absolutely. Contact our sales team for tailored solutions that fit your specific requirements.'
              }
            ].map((faq, idx) => (
              <TiltCard key={idx} intensity={3} className="h-full">
                <GlowingCard className="p-6 rounded-xl h-full border border-blue-500/10">
                  <h4 className="text-white font-medium mb-2">{faq.q}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                </GlowingCard>
              </TiltCard>
            ))}
          </div>
        </motion.div>

        {/* Enterprise CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <TiltCard intensity={3}>
            <div className="glass-premium border border-blue-500/15 rounded-3xl p-12 shadow-2xl">
              <FloatingElement duration={6} yOffset={8}>
                <Zap className="w-12 h-12 text-blue-400 mx-auto mb-4 fill-blue-400/10" />
              </FloatingElement>
              <h3 className="text-2xl font-bold text-white mb-3">Need a Custom Solution?</h3>
              <p className="text-gray-400 mb-6 max-w-lg mx-auto">
                Our enterprise plans are tailored to your organization's unique needs. 
                Get dedicated infrastructure support at scale.
              </p>
              <Button 
                className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] shadow-[0_4px_12px_rgba(59,130,246,0.2)] border-none font-semibold text-white px-8 py-6 rounded-full"
                data-testid="contact-sales-btn"
              >
                Contact Sales
              </Button>
            </div>
          </TiltCard>
        </motion.div>
      </div>
    </div>
  );
}
